import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import Login from './components/Login';
import Signup from './components/Signup';
import Onboarding from './components/Onboarding';
import Dashboard from './components/Dashboard';
import ResidentManagement from './components/ResidentManagement';
import RoomManagement from './components/RoomManagement';
import CaseNotes from './components/CaseNotes';
import MealManagement from './components/MealManagement';
import ChoreManagement from './components/ChoreManagement';
import LaundryManagement from './components/LaundryManagement';
import StaffManagement from './components/StaffManagement';
import TimeReports from './components/TimeReports';
import AdminTimeManagement from './components/AdminTimeManagement';
import BannedResidentManagement from './components/BannedResidentManagement';
import ShiftLogHistory from './components/ShiftLogHistory';
import IncidentReports from './components/IncidentReports';
import MaintenanceRequests from './components/MaintenanceRequests';
import ResidentChangeRequests from './components/ResidentChangeRequests';
import SuperAdmin from './components/SuperAdmin';
import InventoryManagement from './components/InventoryManagement';
import DietaryAccommodations from './components/DietaryAccommodations';
import HmisExports from './components/HmisExports';
import DocumentFolders from './components/DocumentFolders';
import ConsentManagement from './components/ConsentManagement';
import VolunteerManagement from './components/VolunteerManagement';
import HelpAssistant from './components/HelpAssistant';
import Billing from './components/Billing';
import BillingBanner from './components/BillingBanner';
import Navigation from './components/Navigation';
import { Toaster } from './components/ui/sonner';

// Marketing Components
import Homepage from './components/marketing/Homepage';
import MarketingLayout from './components/marketing/MarketingLayout';
import BetaSignup from './components/marketing/BetaSignup';
import FeaturesDetail from './components/marketing/FeaturesDetail';
import AppPreview from './components/marketing/AppPreview';
import Demo from './components/Demo';

// Demo Components
import { DemoBanner, DemoFooter } from './components/DemoConfig';
import DemoSessionBanner from './components/DemoSessionBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  };

  // Validate authentication with backend
  const validateAuth = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Auth validation failed:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔐 Initializing authentication...');
      
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        console.log('🔐 No stored credentials found');
        setLoading(false);
        return;
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        console.log('🔐 Token expired, clearing stored auth');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
        return;
      }

      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Validate with backend
      console.log('🔐 Validating authentication with backend...');
      const validatedUser = await validateAuth(token);
      
      if (validatedUser) {
        console.log('🔐 Authentication validated successfully');
        setUser(validatedUser);
        // Update stored user data if it changed
        localStorage.setItem('user', JSON.stringify(validatedUser));
      } else {
        console.log('🔐 Authentication validation failed, clearing stored auth');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const signup = async (payload) => {
    try {
      const response = await axios.post(`${API}/signup`, payload);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      return { success: true, shelter: response.data.shelter };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Signup failed' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, signup, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Layout Component
const Layout = ({ children }) => {
  const { user } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.is_platform_owner;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation onHelpClick={() => setShowHelp(true)} />
      <main className="pt-16 pb-8">
        {isAdmin && <BillingBanner />}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {children}
        </div>
      </main>

      <DemoSessionBanner />

      {/* AI Help Assistant (admins only) */}
      {isAdmin && <HelpAssistant open={showHelp} setOpen={setShowHelp} />}

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DemoBanner />
        <Routes>
          {/* Marketing Routes */}
          <Route path="/" element={
            <MarketingLayout>
              <Homepage />
            </MarketingLayout>
          } />
          <Route path="/beta" element={
            <MarketingLayout>
              <BetaSignup />
            </MarketingLayout>
          } />
          <Route path="/features" element={
            <MarketingLayout>
              <FeaturesDetail />
            </MarketingLayout>
          } />
          <Route path="/preview" element={
            <MarketingLayout>
              <AppPreview />
            </MarketingLayout>
          } />
          
          {/* Demo Route */}
          <Route path="/demo" element={<Demo />} />
          
          {/* App Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/residents" element={
            <ProtectedRoute>
              <Layout>
                <ResidentManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rooms" element={
            <ProtectedRoute>
              <Layout>
                <RoomManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/case-notes" element={
            <ProtectedRoute>
              <Layout>
                <CaseNotes />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/change-requests" element={
            <ProtectedRoute>
              <Layout>
                <ResidentChangeRequests />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/platform" element={
            <ProtectedRoute>
              <Layout>
                <SuperAdmin />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <Layout>
                <Billing />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/meals" element={
            <ProtectedRoute>
              <Layout>
                <MealManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <Layout>
                <InventoryManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/dietary" element={
            <ProtectedRoute>
              <Layout>
                <DietaryAccommodations />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/volunteers" element={
            <ProtectedRoute>
              <Layout>
                <VolunteerManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/consent" element={
            <ProtectedRoute>
              <Layout>
                <ConsentManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/hmis-exports" element={
            <ProtectedRoute>
              <Layout>
                <HmisExports />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <Layout>
                <DocumentFolders />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/chores" element={
            <ProtectedRoute>
              <Layout>
                <ChoreManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/laundry" element={
            <ProtectedRoute>
              <Layout>
                <LaundryManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/staff" element={
            <ProtectedRoute>
              <Layout>
                <StaffManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/time-reports" element={
            <ProtectedRoute>
              <Layout>
                <TimeReports />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin-time-management" element={
            <ProtectedRoute>
              <Layout>
                <AdminTimeManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/banned-residents" element={
            <ProtectedRoute>
              <Layout>
                <BannedResidentManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/shift-logs" element={
            <ProtectedRoute>
              <Layout>
                <ShiftLogHistory />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/incident-reports" element={
            <ProtectedRoute>
              <Layout>
                <IncidentReports />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/maintenance-requests" element={
            <ProtectedRoute>
              <Layout>
                <MaintenanceRequests />
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
        <DemoFooter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;